























import {ref, reactive, computed, defineComponent, PropType} from '@vue/composition-api'
import {clamp} from '@horizon/common/utils'
import {FloatParamDef} from '@/params'

// State machine-based implementation
// onXYZ functions handle input events
// process/start/endXYZ functions are the state transition actions

/* eslint-disable @typescript-eslint/no-use-before-define */

// focused example: tabbing in to field.
// blurred example: on end drag, or Enter, or tab out
type UIState = 'blurred'|'focused'|'mouseDown'|'dragging'|'typing'
type EventType = 'focus'|'key'|'enter'|'mouseDown'|'mouseUp'|'drag'|'blur'

type StateType = {
  mouseX: number|null;
  uiState: UIState;
  typedValue: number|null;
}

export default defineComponent({
  name: 'DraggableNumberInput',
  props: {
    value: {
      type: Number,
      required: true
    },
    index: {
      type: Number,
      required: false,
      default: -1
    },
    paramDef: {
      type: Object as PropType<FloatParamDef>,
      required: true,
    }
  },
  setup(props, context) {
    const state: StateType = reactive({
      mouseX: null,
      uiState: 'blurred',
      // Value being typed in the text field
      typedValue: null
    })

    // state machine
    function processEvent(transition: EventType, event?: any) {
      const oldState = state.uiState
      // console.log(`State transition start: ${oldState} + ${transition} => ...`)
      switch (state.uiState) {
      case 'blurred':
        if (transition=='focus') {
          state.uiState = 'focused'
        } else if (transition=='key') {
          state.uiState = 'typing'
        } else if (transition=='mouseDown') {
          saveMouseX(event)
          state.uiState = 'mouseDown'
        }
        break
      case 'focused':        // just focused but nothing happening
        if (transition=='blur') {
          // nothing typed yet, nothing to do
          state.uiState = 'blurred'
        } else if (transition=='key') {
          processKey(event)
          state.uiState = 'typing'
        } else if (transition=='mouseDown') {
          saveMouseX(event)
          state.uiState = 'mouseDown'
        }
        break
      case 'mouseDown':
        if (transition=='blur') {
          // ignore mouse state
          state.uiState = 'blurred'
        } else if (transition=='key') { // switch to typing mode
          processKey(event)
          state.uiState = 'typing'
        } else if (transition=='mouseUp') {
          selectAllText()
          state.uiState = 'typing' // no drag: enter typing mode
        } else if (transition=='drag') {
          startDrag(event)
          state.uiState = 'dragging'
        }
        break
      case 'dragging':
        if (transition=='blur') {
          endDrag(event)
          state.uiState = 'blurred'
        } else if (transition=='drag') {
          processDrag(event)
        } else if (transition=='key') { // switch to typing mode (unusual!)
          endDrag(event)
          processKey(event)
          state.uiState = 'typing'
        } else if (transition=='mouseUp') {
          endDrag(event)
          state.uiState = 'focused'
        }
        break
      case 'typing':
        if (transition=='blur') {
          emitValue()
          state.uiState = 'blurred'
        } else if (transition=='key') {
          processKey(event)
        } else if (transition=='enter') {
          emitValue()                         // stay in typing state
        } else if (transition=='mouseDown') { // switch to drag mode
          saveMouseX(event)
          emitValue()
          state.uiState = 'mouseDown'
        }
        break
      }
      // console.log(`State transition: ${oldState} + ${transition} => ${state.uiState}`)
    }

    const numberField = ref(null) // DOM ref

    // Current value, rounded to the given number of decimal places
    const displayValue = computed(() => {
      const decimals = props.paramDef?.decimalPlaces ?? 2
      return Number(props.value).toFixed(decimals)
    })

    function numberChanged(value: number) {
      state.typedValue = value  // ???
    }

    function selectAllText() {
      // Focus and select text for keyboard input.
      // XXX: is there an actual type for this?
      const input = numberField.value as any
      if (!input)
        return
      input.focus()
      // XXX: may not work on mobile Safari
      // https://stackoverflow.com/questions/4067469/selecting-all-text-in-html-text-input-when-clicked
      input.$refs.input.select()
    }

    function onFocus() {
      processEvent('focus')
    }

    /** On blur, accept the typed input */
    function onBlur() {
      processEvent('blur')
      state.uiState = 'blurred'
    }

    function onKey(event: KeyboardEvent) {
      processEvent('key', event)
    }

    function onEnterKey(event: KeyboardEvent) {
      processEvent('enter', event)
    }

    function startTyping() {
      selectAllText()
      const input = numberField.value as any
    }

    function processKey(event: KeyboardEvent) {
      // nothing needed
    }

    function emitValue() {
      if (state.typedValue === null)
        return
      const input = numberField.value as any
      context.emit('startDrag')
      if (props.paramDef)
        context.emit('input', clamp(state.typedValue, props.paramDef.min, props.paramDef.max))
      else
        context.emit('input', state.typedValue)
      state.typedValue = null
    }

    function getIncrement(prevX: number, event: MouseEvent) {
      const step = props.paramDef?.step ?? 0.01
      const baseValue = step * (event.x - prevX)
      if (event.ctrlKey)
        return baseValue * 0.1

      else if (event.shiftKey)
        return baseValue * 5

      else
        return baseValue
    }

    // The minimum visible input change
    const visibleStep = computed(() => {
      const decimals = props.paramDef?.decimalPlaces ?? 2
      return Math.pow(10, -1 * decimals)
    })

    function startDrag(event: MouseEvent) {
      context.emit('startDrag')
      document.body.style.cursor = "e-resize"
      processDrag(event)
    }

    function endDrag(_event: MouseEvent) {
      state.mouseX = null
      document.body.style.cursor = "default"
    }

    function processDrag(event: MouseEvent) {
      if (state.mouseX === null) {
        // XXX best way to report errors?
        console.warn('WARNING, drag with no previous mouse position')
        return
      }
      const increment = getIncrement(state.mouseX, event)
      if (Math.abs(increment) >= visibleStep.value) {
        state.mouseX = event.x
        let val = props.value + increment
        if (props.paramDef)
          val = clamp(val, props.paramDef.min, props.paramDef.max)
        context.emit('input', val)
      }
    }

    function saveMouseX(event: MouseEvent) {
      state.mouseX = event.x
    }

    function onDrag(event: MouseEvent) {
      processEvent('drag', event)
    }

    function onMouseUp(event: MouseEvent) {
      state.mouseX = null
      window.removeEventListener('mousemove', onDrag)
      window.removeEventListener('mouseup', onMouseUp)
      event.preventDefault()
      processEvent('mouseUp', event)
    }

    function onMouseDown(event: MouseEvent) {
      state.mouseX = event.x
      window.addEventListener('mousemove', onDrag)
      window.addEventListener('mouseup', onMouseUp)
      event.preventDefault()
      processEvent('mouseDown', event)
    }

    return ({displayValue, visibleStep, numberChanged, numberField,
             startTyping, onEnterKey, emitValue, onFocus, onBlur, onMouseDown, onDrag, endDrag,
             onKey, state})
  }
})
